import { Injectable, inject } from '@angular/core';
import { Observable, BehaviorSubject, map, filter } from 'rxjs';
import { UsersService } from '../users/user.service';
import { UserPrivilegesResponse, Department } from '../../models/sdk-interfaces';

const ITP_ADMIN_ROLE_KEY = 'ITP Admin';
const ITP_PROCESSOR_ROLE_KEYS: readonly string[] = ['ITP Processor', 'ITP HS Processor'];

@Injectable({
  providedIn: 'root'
})
export class UserPrivilegesService {
  private userService = inject(UsersService);

  private privilegesSubject = new BehaviorSubject<UserPrivilegesResponse | undefined>(undefined);
  private cacheTimestamp: number = 0;
  private readonly CACHE_TTL = 5 * 60 * 1000;

  /**
   * Fetches the user's privileges, utilizing a cache to minimize API calls. If the cache is valid, it returns the cached privileges; otherwise, it fetches fresh data from the API.
   * @param accessToken - The access token used to authenticate the API request for fetching user privileges.
   */
  getUserPrivileges(accessToken: string): Promise<UserPrivilegesResponse> {
    const privsTimeStamp = Date.now();
    const cachedPrivs = this.privilegesSubject.value;

    if (cachedPrivs && privsTimeStamp - this.cacheTimestamp < this.CACHE_TTL) {
      return Promise.resolve(cachedPrivs);
    }

    return this.userService.getCurrentUserPrivileges(accessToken).then(privileges => {
      this.cacheTimestamp = privsTimeStamp;
      this.privilegesSubject.next(privileges);
      return privileges;
    });
  }

  /**
   * Manually sets the user's privileges in the service, updating the cache timestamp to the current time. This can be used to update privileges after a successful API call or when privileges are modified.
   * @param privileges - The user privileges to be set and cached.
   */
  setPrivileges(privileges: UserPrivilegesResponse): void {
    this.cacheTimestamp = Date.now();
    this.privilegesSubject.next(privileges);
  }

  /**
   * Gets the current user privileges from the subject
   * @returns The current user privileges or undefined if not set
   */
  getCurrentPrivileges(): UserPrivilegesResponse | undefined {
    return this.privilegesSubject.value;
  }

  /**
   * Checks whether the user has the ITP Admin role.
   * @param roles - The user's roles.
   */
  hasItpAdminRole(roles: ReadonlyArray<{ key: string }> | null | undefined): boolean {
    return !!roles?.some(role => role.key === ITP_ADMIN_ROLE_KEY);
  }

  /**
   * Checks whether the user has any ITP Processor role (college or high school), which is
   * required to access transcript processing.
   * @param roles - The user's roles.
   */
  hasItpProcessorRole(roles: ReadonlyArray<{ key: string }> | null | undefined): boolean {
    return !!roles?.some(role => ITP_PROCESSOR_ROLE_KEYS.includes(role.key));
  }

  /**
   * Clears the cached user privileges and resets the cache timestamp.
   */
  clearCache(): void {
    this.cacheTimestamp = 0;
    this.privilegesSubject.next(undefined);
  }

  /**
   * Determines if the user has the necessary privileges to perform a specific action, optionally within a certain department and status context. It checks both application-level and resource-level privileges to make this determination.
   * @param privilegeCode - The code representing the specific privilege required to perform the action.
   * @param department  - (Optional) The department context in which the action is being performed. If provided, the method checks if the user has privileges within this department.
   * @param status - (Optional) The status context in which the action is being performed. If provided, the method checks if the user's privileges within the specified department include this status.
   */
  canPerformAction(
    privilegeCode: string,
    department?: string,
    status?: string
  ): Observable<boolean> {
    return this.privilegesSubject.asObservable().pipe(
      map(privileges => {
        if (!privileges) return false;
        if (privileges.applicationPrivileges?.some(p => p.code === privilegeCode)) {
          return !department;
        }

        // Find the privilege by operationName
        const privilege = privileges.resourcePrivileges.find(
          p => p.operationName === privilegeCode
        );
        if (!privilege) {
          return false;
        }

        // If department is specified, find that department inside the privilege
        if (department) {
          const dep = privilege.context.departments.find(d => d.code === department);
          if (!dep) {
            // Department not in this privilege => deny
            return false;
          }

          // If status is specified, check if that status is allowed for this department
          if (status && dep.status && dep.status.length) {
            return dep.status?.includes(status) || false;
          } else {
            // If no specific status is required or if status list is empty, grant privilege
            return true;
          }
        }

        // If no department is specified at all, then having the privilege is enough
        return true;
      })
    );
  }

  /**
   * Retrieves all departments from the user's resource privileges.
   */
  getAllDepartments(): Observable<Department[]> {
    return this.privilegesSubject.asObservable().pipe(
      filter(Boolean),
      map(privileges => {
        const departmentArray: Department[] = [];

        privileges.resourcePrivileges.forEach(privilege => {
          privilege.context.departments.forEach(department => {
            if (!departmentArray.find(d => d.code === department.code)) {
              departmentArray.push(department);
            }
          });
        });

        return departmentArray;
      })
    );
  }

  /**
   * Retrieves departments associated with a specific privilege code from the user's resource privileges.
   * @param privilegeCode - The code representing the specific privilege for which to retrieve associated departments.
   */
  getDepartmentsByPrivilege(privilegeCode: string): Observable<Department[]> {
    return this.privilegesSubject.asObservable().pipe(
      filter(Boolean),
      map(privileges => {
        const departmentArray: Department[] = [];

        const privilege = privileges.resourcePrivileges.find(
          p => p.operationName === privilegeCode
        );
        if (privilege) {
          privilege.context.departments.forEach(department => {
            if (!departmentArray.find(d => d.code === department.code)) {
              departmentArray.push(department);
            }
          });
        }

        return departmentArray;
      })
    );
  }
}
